import { useEffect, useState } from 'react';

/**
 * Whether this visitor should be shown motion.
 *
 * Two independent signals have to be honored, and they fail in different ways
 * if you only check one:
 *
 * 1. `prefers-reduced-motion` is the operating system setting. Vestibular
 *    disorders are common and this is how people tell every site at once.
 *
 * 2. The site's own accessibility widget has a "Pause Animations" toggle,
 *    which adds `pause-animations` to <body>. Its stylesheet kills CSS
 *    `animation` and `transition`, and that is enough for CSS-driven motion.
 *    It is NOT enough here: Framer Motion animates inline styles frame by
 *    frame, so it sails straight through a `transition: none` rule. If we did
 *    not watch for the class in JavaScript, a visitor could switch animations
 *    off and watch the page keep moving, which is worse than never offering
 *    the control.
 *
 * The class is watched rather than read once, so toggling the widget takes
 * effect immediately instead of on the next page load.
 */
// Read the settings synchronously so the very first render is already right.
// Starting from `true` and correcting in an effect meant a visitor with reduced
// motion switched on could see entrances begin and then be cut off mid-way.
// The accessibility widget restores its class in its own effect, after this
// first render, so that case is still caught by the observer below.
const readAllowed = () => {
  if (typeof window === 'undefined') return true;
  return (
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
    !document.body.classList.contains('pause-animations')
  );
};

export default function useMotionAllowed() {
  const [allowed, setAllowed] = useState(readAllowed);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');

    const evaluate = () =>
      setAllowed(
        !query.matches && !document.body.classList.contains('pause-animations')
      );

    evaluate();

    query.addEventListener('change', evaluate);

    // The widget toggles a class on <body>, so watch that attribute directly.
    const observer = new MutationObserver(evaluate);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      query.removeEventListener('change', evaluate);
      observer.disconnect();
    };
  }, []);

  return allowed;
}
